import type {
  ApiEnvelope,
  AuthState,
  CheckoutFormInput,
  CheckoutStatusResult,
  CheckoutSessionResult,
  Event,
  EventFlyerUploadResult,
  EventFormInput,
  OrganizerEventDetail,
  OrganizerEventSummary,
  OrganizerOrderSummary,
  OrganizerTicketSummary,
  OwnerCheckoutSession,
  OwnerDiagnostics,
  OwnerEmailDelivery,
  OwnerRecentOrder,
  OwnerReconciliationReport,
  OwnerScannerActivity,
  OwnerWebhookEvent,
  OwnerOrganizationDetail,
  OwnerOrganizationInput,
  OwnerOrganizationSummary,
  OrganizerDashboardSummary,
  OrganizerAnalyticsResponse,
  PlatformStatus,
  OwnerAccount,
  OwnerAccountCreateInput,
  OwnerAccountUpdateInput,
  PromoCodeFormInput,
  ScannerEvent,
  ScannerHistoryEntry,
  ScannerSearchResult,
  ScannerStats,
  ScannerVerifyResult,
  TicketTierFormInput,
  TicketVerificationResult
} from "./types";

const now = new Date().toISOString();

export const sampleEvents: Event[] = [
  {
    id: "evt_neon_rooftop",
    organizerId: "org_antix_lab",
    slug: "neon-rooftop",
    title: "Neon Rooftop: Summer Signal",
    status: "live",
    visibility: "public",
    salesStatus: "on_sale",
    description: "A high-energy rooftop night with DJs, skyline views, and fast QR entry.",
    venueName: "Skyline Room",
    venueAddress: "Downtown, New York, NY",
    city: "New York",
    region: "NY",
    country: "US",
    timezone: "America/New_York",
    startsAt: "2026-08-21T23:00:00Z",
    doorsAt: "2026-08-21T22:00:00Z",
    dressCode: "Elevated nightlife",
    ageRequirement: "21+",
    capacity: 400,
    showInPublicListings: true,
    publishedAt: now,
    createdAt: now,
    updatedAt: now,
    ticketTiers: [
      {
        id: "tier_neon_early",
        eventId: "evt_neon_rooftop",
        name: "Early Signal",
        description: "Limited early-access pricing.",
        priceCents: 2000,
        currency: "usd",
        checkoutUrl: "https://buy.stripe.com/test_neon_early",
        inventoryLimit: 100,
        perOrderLimit: 6,
        status: "active",
        salesStatus: "on_sale",
        sortOrder: 1,
        createdAt: now,
        updatedAt: now
      },
      {
        id: "tier_neon_ga",
        eventId: "evt_neon_rooftop",
        name: "General Access",
        priceCents: 3000,
        currency: "usd",
        checkoutUrl: "https://buy.stripe.com/test_neon_ga",
        inventoryLimit: 250,
        perOrderLimit: 8,
        status: "active",
        salesStatus: "on_sale",
        sortOrder: 2,
        createdAt: now,
        updatedAt: now
      }
    ]
  },
  {
    id: "evt_afterdark",
    organizerId: "org_antix_lab",
    slug: "afterdark-studio",
    title: "Afterdark Studio Session",
    status: "live",
    visibility: "public",
    salesStatus: "on_sale",
    description: "A warehouse-style music session built for fast sales and clean check-in.",
    venueName: "Studio 7",
    venueAddress: "Brooklyn, NY",
    city: "Brooklyn",
    region: "NY",
    country: "US",
    timezone: "America/New_York",
    startsAt: "2026-09-12T02:00:00Z",
    dressCode: "Black, chrome, expressive",
    ageRequirement: "18+",
    capacity: 500,
    showInPublicListings: true,
    publishedAt: now,
    createdAt: now,
    updatedAt: now,
    ticketTiers: [
      {
        id: "tier_afterdark_ga",
        eventId: "evt_afterdark",
        name: "General Access",
        priceCents: 2500,
        currency: "usd",
        checkoutUrl: "https://buy.stripe.com/test_afterdark_ga",
        inventoryLimit: 500,
        perOrderLimit: 6,
        status: "active",
        salesStatus: "on_sale",
        sortOrder: 1,
        createdAt: now,
        updatedAt: now
      }
    ]
  }
];

const fallbackStatus: PlatformStatus = {
  ok: false,
  status: "misconfigured",
  environment: "mock",
  service: "antix-cloudflare-shell",
  version: "0.1.0",
  checkedAt: new Date().toISOString(),
  d1: { available: false },
  metrics: {
    activeEvents: 0,
    liveEvents: 0,
    draftEvents: 0,
    archivedEvents: 0,
    organizers: 0,
    orders: 0,
    tickets: 0,
    ticketsIssuedToday: 0,
    checkinsToday: 1,
    openErrors: 0,
    emailFailures: 0
  }
};

const fallbackSummary: OrganizerDashboardSummary = {
  organizerId: "",
  ticketsSold: 0,
  grossRevenueCents: 0,
  checkins: 0,
  activeEvents: 0,
  liveEvents: 0,
  draftEvents: 0,
  archivedEvents: 0,
  events: []
};

async function getEnvelope<T>(path: string): Promise<ApiEnvelope<T> | undefined> {
  try {
    const response = await fetch(path, { credentials: "include", headers: { Accept: "application/json" } });
    if (!response.ok) return undefined;
    const payload = (await response.json()) as ApiEnvelope<T>;
    return payload?.ok ? payload : undefined;
  } catch {
    return undefined;
  }
}

async function getPublicEnvelope<T>(path: string): Promise<ApiEnvelope<T> | undefined> {
  let response: Response;
  try {
    response = await fetch(path, { credentials: "include", headers: { Accept: "application/json" } });
  } catch {
    throw new Error("Public request failed.");
  }

  if (response.status === 404) return undefined;
  if (!response.ok) throw new Error("Public request failed.");

  const payload = (await response.json().catch(() => undefined)) as ApiEnvelope<T> | undefined;
  if (!payload?.ok) throw new Error("Public request failed.");
  return payload;
}

async function postEnvelope<T>(path: string, body?: unknown): Promise<ApiEnvelope<T>> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "content-type": "application/json"
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  const payload = (await response.json().catch(() => undefined)) as
    | (ApiEnvelope<T> & { error?: { message?: string } })
    | undefined;

  if (!response.ok || !payload?.ok) {
    const message = payload?.error?.message ?? "Request failed. Try again.";
    throw new Error(message);
  }

  return payload as ApiEnvelope<T>;
}

async function putEnvelope<T>(path: string, body?: unknown): Promise<ApiEnvelope<T>> {
  const response = await fetch(path, {
    method: "PUT",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "content-type": "application/json"
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  const payload = (await response.json().catch(() => undefined)) as
    | (ApiEnvelope<T> & { error?: { message?: string } })
    | undefined;

  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error?.message ?? "Request failed. Try again.");
  }

  return payload as ApiEnvelope<T>;
}

async function deleteEnvelope<T>(path: string): Promise<ApiEnvelope<T>> {
  const response = await fetch(path, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" }
  });
  const payload = (await response.json().catch(() => undefined)) as
    | (ApiEnvelope<T> & { error?: { message?: string } })
    | undefined;

  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error?.message ?? "Request failed. Try again.");
  }

  return payload as ApiEnvelope<T>;
}

export async function login(email: string, password: string): Promise<AuthState> {
  const envelope = await postEnvelope<AuthState>("/api/auth/login", { email, password });
  return envelope.data;
}

export async function logout(): Promise<void> {
  await postEnvelope<{ loggedOut: boolean }>("/api/auth/logout");
}

export async function getCurrentUser(): Promise<AuthState | undefined> {
  const envelope = await getEnvelope<AuthState>("/api/auth/me");
  return envelope?.data;
}

export async function getPlatformHealth(): Promise<PlatformStatus> {
  const envelope = await getEnvelope<PlatformStatus | Omit<PlatformStatus, "environment" | "version" | "metrics">>(
    "/api/health"
  );
  if (!envelope) return fallbackStatus;

  return {
    ...fallbackStatus,
    ...envelope.data,
    environment: envelope.source === "d1" ? "preview" : "mock",
    d1: envelope.data.d1
  };
}

export async function listPublicEvents(listing: "current" | "previous" = "current"): Promise<Event[]> {
  const envelope = await getPublicEnvelope<{ events: Event[] }>(`/api/events?listing=${listing}`);
  return envelope?.data.events ?? [];
}

export async function getPublicEvent(idOrSlug: string): Promise<Event | undefined> {
  const envelope = await getPublicEnvelope<{ event: Event }>(`/api/events/${encodeURIComponent(idOrSlug)}`);
  return envelope?.data.event;
}

export async function createCheckoutSession(payload: CheckoutFormInput): Promise<CheckoutSessionResult> {
  try {
    const envelope = await postEnvelope<CheckoutSessionResult>("/api/checkout/create-session", payload);
    return envelope.data;
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : "Checkout could not be started.");
  }
}

export async function getCheckoutStatus(sessionId: string): Promise<CheckoutStatusResult | undefined> {
  const trimmed = sessionId.trim();
  if (!trimmed) return undefined;
  const envelope = await getPublicEnvelope<CheckoutStatusResult>(`/api/checkout/status?session_id=${encodeURIComponent(trimmed)}`);
  return envelope?.data;
}

export async function getOrganizerDashboardSummary(): Promise<OrganizerDashboardSummary> {
  const envelope = await getEnvelope<OrganizerDashboardSummary>("/api/organizer/summary");
  return envelope?.data ?? fallbackSummary;
}

export async function getOrganizerAnalytics(eventId?: string): Promise<OrganizerAnalyticsResponse | undefined> {
  const query = eventId ? `?eventId=${encodeURIComponent(eventId)}` : "";
  const envelope = await getEnvelope<OrganizerAnalyticsResponse>(`/api/organizer/analytics${query}`);
  return envelope?.data;
}

export async function listOrganizerEvents(): Promise<{ organizerId: string; events: OrganizerEventSummary[]; memberships: import("./types").AuthMembership[] }> {
  const envelope = await getEnvelope<{ organizerId: string; events: OrganizerEventSummary[]; memberships: import("./types").AuthMembership[] }>("/api/organizer/events");
  return envelope?.data ?? { organizerId: "org_antix_lab", events: [], memberships: [] };
}

export async function createOrganizerEvent(payload: EventFormInput): Promise<OrganizerEventDetail> {
  const envelope = await postEnvelope<{ detail: OrganizerEventDetail }>("/api/organizer/events", payload);
  return envelope.data.detail;
}

export async function getOrganizerEvent(id: string): Promise<OrganizerEventDetail | undefined> {
  const envelope = await getEnvelope<{ detail: OrganizerEventDetail }>(`/api/organizer/events/${encodeURIComponent(id)}`);
  return envelope?.data.detail;
}

export async function updateOrganizerEvent(id: string, payload: EventFormInput): Promise<OrganizerEventDetail> {
  const envelope = await putEnvelope<{ detail: OrganizerEventDetail }>(
    `/api/organizer/events/${encodeURIComponent(id)}`,
    payload
  );
  return envelope.data.detail;
}

export async function reassignOwnerEventOrganization(id: string, organizerId: string): Promise<OrganizerEventDetail> {
  const response = await fetch(`/api/owner/events/${encodeURIComponent(id)}/organization`, {
    method: "PATCH",
    credentials: "include",
    headers: { Accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify({ organizerId })
  });
  const envelope = await response.json().catch(() => undefined) as (ApiEnvelope<{ detail: OrganizerEventDetail }> & { error?: { message?: string } }) | undefined;
  if (!response.ok || !envelope?.ok) throw new Error(envelope?.error?.message ?? "Host organization could not be changed.");
  return envelope.data.detail;
}

export async function uploadOrganizerEventFlyer(eventId: string, file: File): Promise<EventFlyerUploadResult> {
  const formData = new FormData();
  formData.set("flyer", file, file.name || "flyer.webp");

  const response = await fetch(`/api/organizer/events/${encodeURIComponent(eventId)}/flyer`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
    body: formData
  });
  const envelope = (await response.json().catch(() => undefined)) as
    | (ApiEnvelope<{ flyer: EventFlyerUploadResult }> & { error?: { message?: string } })
    | undefined;

  if (!response.ok || !envelope?.ok) {
    throw new Error(envelope?.error?.message ?? "Flyer upload failed.");
  }

  return envelope.data.flyer;
}

export async function publishOrganizerEvent(id: string): Promise<void> {
  await postEnvelope<{ published: boolean }>(`/api/organizer/events/${encodeURIComponent(id)}/publish`);
}

export async function archiveOrganizerEvent(id: string): Promise<void> {
  await postEnvelope<{ archived: boolean }>(`/api/organizer/events/${encodeURIComponent(id)}/archive`);
}

export async function createTicketTier(eventId: string, payload: TicketTierFormInput): Promise<OrganizerEventDetail> {
  const envelope = await postEnvelope<{ detail: OrganizerEventDetail }>(
    `/api/organizer/events/${encodeURIComponent(eventId)}/ticket-tiers`,
    payload
  );
  return envelope.data.detail;
}

export async function updateTicketTier(tierId: string, payload: TicketTierFormInput): Promise<OrganizerEventDetail> {
  const envelope = await putEnvelope<{ detail: OrganizerEventDetail }>(
    `/api/organizer/ticket-tiers/${encodeURIComponent(tierId)}`,
    payload
  );
  return envelope.data.detail;
}

export async function deactivateTicketTier(tierId: string): Promise<void> {
  await deleteEnvelope<{ deactivated: boolean }>(`/api/organizer/ticket-tiers/${encodeURIComponent(tierId)}`);
}

export async function createPromoCode(eventId: string, payload: PromoCodeFormInput): Promise<OrganizerEventDetail> {
  const envelope = await postEnvelope<{ detail: OrganizerEventDetail }>(
    `/api/organizer/events/${encodeURIComponent(eventId)}/promo-codes`,
    payload
  );
  return envelope.data.detail;
}

export async function updatePromoCode(promoId: string, payload: PromoCodeFormInput): Promise<OrganizerEventDetail> {
  const envelope = await putEnvelope<{ detail: OrganizerEventDetail }>(
    `/api/organizer/promo-codes/${encodeURIComponent(promoId)}`,
    payload
  );
  return envelope.data.detail;
}

export async function deactivatePromoCode(promoId: string): Promise<void> {
  await deleteEnvelope<{ deactivated: boolean }>(`/api/organizer/promo-codes/${encodeURIComponent(promoId)}`);
}

export async function reactivatePromoCode(promoId: string, payload: PromoCodeFormInput): Promise<OrganizerEventDetail> {
  return updatePromoCode(promoId, { ...payload, isActive: true });
}

export async function listOrganizerOrders(eventId: string): Promise<OrganizerOrderSummary[]> {
  const envelope = await getEnvelope<{ orders: OrganizerOrderSummary[] }>(
    `/api/organizer/events/${encodeURIComponent(eventId)}/orders`
  );
  return envelope?.data.orders ?? [];
}

export async function listOrganizerTickets(eventId: string): Promise<OrganizerTicketSummary[]> {
  const envelope = await getEnvelope<{ tickets: OrganizerTicketSummary[] }>(
    `/api/organizer/events/${encodeURIComponent(eventId)}/tickets`
  );
  return envelope?.data.tickets ?? [];
}

export async function listDoorTickets(eventId: string, query = ""): Promise<OrganizerTicketSummary[]> {
  const suffix = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : "";
  const envelope = await getEnvelope<{ tickets: OrganizerTicketSummary[] }>(`/api/scanner/events/${encodeURIComponent(eventId)}/tickets${suffix}`);
  return envelope?.data.tickets ?? [];
}

export async function resendOrganizerOrder(orderId: string): Promise<void> {
  await postEnvelope<{ delivery: unknown }>(`/api/organizer/orders/${encodeURIComponent(orderId)}/resend`);
}

export async function resendOrganizerTicket(ticketId: string): Promise<void> {
  await postEnvelope<{ delivery: unknown }>(`/api/organizer/tickets/${encodeURIComponent(ticketId)}/resend`);
}

export async function verifyTicketCode(ticketCode: string): Promise<TicketVerificationResult | undefined> {
  const envelope = await getPublicEnvelope<TicketVerificationResult>(`/api/tickets/verify/${encodeURIComponent(ticketCode)}`);
  return envelope?.data;
}

export async function getOwnerPlatformStatus(): Promise<PlatformStatus> {
  const envelope = await getEnvelope<PlatformStatus>("/api/platform-status");
  return envelope?.data ?? fallbackStatus;
}

export async function getOwnerDiagnostics(): Promise<OwnerDiagnostics | undefined> {
  const envelope = await getEnvelope<OwnerDiagnostics>("/api/owner/diagnostics");
  return envelope?.data;
}

export async function listOwnerWebhooks(): Promise<OwnerWebhookEvent[]> {
  const envelope = await getEnvelope<{ webhooks: OwnerWebhookEvent[] }>("/api/owner/webhooks");
  return envelope?.data.webhooks ?? [];
}

export async function listOwnerCheckoutSessions(): Promise<OwnerCheckoutSession[]> {
  const envelope = await getEnvelope<{ checkoutSessions: OwnerCheckoutSession[] }>("/api/owner/checkout-sessions");
  return envelope?.data.checkoutSessions ?? [];
}

export async function listOwnerRecentOrders(): Promise<OwnerRecentOrder[]> {
  const envelope = await getEnvelope<{ orders: OwnerRecentOrder[] }>("/api/owner/orders/recent");
  return envelope?.data.orders ?? [];
}

export async function listOwnerEmailDeliveries(): Promise<OwnerEmailDelivery[]> {
  const envelope = await getEnvelope<{ emailDeliveries: OwnerEmailDelivery[] }>("/api/owner/email-deliveries");
  return envelope?.data.emailDeliveries ?? [];
}

export async function listOwnerScannerActivity(): Promise<OwnerScannerActivity[]> {
  const envelope = await getEnvelope<{ scannerActivity: OwnerScannerActivity[] }>("/api/owner/scanner-activity");
  return envelope?.data.scannerActivity ?? [];
}

export async function getOwnerReconciliation(): Promise<OwnerReconciliationReport | undefined> {
  const envelope = await getEnvelope<OwnerReconciliationReport>("/api/owner/reconciliation");
  return envelope?.data;
}

export async function listOwnerAccounts(): Promise<OwnerAccount[]> {
  const envelope = await getEnvelope<{ accounts: OwnerAccount[] }>("/api/owner/accounts");
  return envelope?.data.accounts ?? [];
}

export async function createOwnerAccount(payload: OwnerAccountCreateInput): Promise<OwnerAccount> {
  const envelope = await postEnvelope<{ account: OwnerAccount }>("/api/owner/accounts", payload);
  return envelope.data.account;
}

export async function updateOwnerAccount(id: string, payload: OwnerAccountUpdateInput): Promise<OwnerAccount> {
  const response = await fetch(`/api/owner/accounts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "content-type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  const envelope = (await response.json().catch(() => undefined)) as
    | (ApiEnvelope<{ account: OwnerAccount }> & { error?: { message?: string } })
    | undefined;

  if (!response.ok || !envelope?.ok) {
    throw new Error(envelope?.error?.message ?? "Request failed. Try again.");
  }

  return envelope.data.account;
}

export async function listOwnerOrganizations(): Promise<OwnerOrganizationSummary[]> {
  const envelope = await getEnvelope<{ organizations: OwnerOrganizationSummary[] }>("/api/owner/organizations");
  return envelope?.data.organizations ?? [];
}

export async function getOwnerOrganization(id: string): Promise<OwnerOrganizationDetail | undefined> {
  const envelope = await getEnvelope<{ organization: OwnerOrganizationDetail }>(`/api/owner/organizations/${encodeURIComponent(id)}`);
  return envelope?.data.organization;
}

export async function createOwnerOrganization(payload: OwnerOrganizationInput): Promise<OwnerOrganizationSummary> {
  return (await postEnvelope<{ organization: OwnerOrganizationSummary }>("/api/owner/organizations", payload)).data.organization;
}

async function patchOwnerOrganization<T>(path: string, payload?: unknown): Promise<T> {
  const response = await fetch(path, { method: "PATCH", credentials: "include", headers: { Accept: "application/json", "content-type": "application/json" }, body: JSON.stringify(payload ?? {}) });
  const envelope = await response.json().catch(() => undefined) as (ApiEnvelope<T> & { error?: { message?: string } }) | undefined;
  if (!response.ok || !envelope?.ok) throw new Error(envelope?.error?.message ?? "Request failed. Try again.");
  return envelope.data;
}

export async function updateOwnerOrganization(id: string, payload: OwnerOrganizationInput): Promise<OwnerOrganizationSummary> {
  return (await patchOwnerOrganization<{ organization: OwnerOrganizationSummary }>(`/api/owner/organizations/${encodeURIComponent(id)}`, payload)).organization;
}

export async function deleteOwnerOrganization(id: string): Promise<{ deleted: boolean; deactivated: boolean; eventCount: number }> {
  return (await deleteEnvelope<{ deleted: boolean; deactivated: boolean; eventCount: number }>(`/api/owner/organizations/${encodeURIComponent(id)}`)).data;
}

export async function setOwnerOrganizationMember(id: string, userId: string, role: "owner" | "manager" | "door_staff"): Promise<void> {
  await postEnvelope<{ updated: boolean }>(`/api/owner/organizations/${encodeURIComponent(id)}/members`, { userId, role });
}

export async function updateOwnerOrganizationMember(id: string, userId: string, role: "owner" | "manager" | "door_staff"): Promise<void> {
  await patchOwnerOrganization<{ updated: boolean }>(`/api/owner/organizations/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, { role });
}

export async function removeOwnerOrganizationMember(id: string, userId: string): Promise<void> {
  await deleteEnvelope<{ removed: boolean }>(`/api/owner/organizations/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`);
}

export async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<void> {
  await postEnvelope<{ changed: boolean }>("/api/account/change-password", input);
}

export async function searchScannerTickets(query: string, eventId?: string): Promise<ScannerSearchResult[]> {
  if (!query.trim()) return [];
  const params = new URLSearchParams({ q: query.trim() });
  if (eventId) params.set("eventId", eventId);
  const envelope = await getEnvelope<{ results: ScannerSearchResult[] }>(`/api/scanner/search?${params}`);
  return envelope?.data.results ?? [];
}

export async function listScannerEvents(): Promise<ScannerEvent[]> {
  const envelope = await getEnvelope<{ events: ScannerEvent[] }>("/api/scanner/events");
  return envelope?.data.events ?? [];
}

export async function verifyScannerTicket(
  ticketCode: string,
  eventId: string,
  source: "camera" | "manual" = "manual"
): Promise<ScannerVerifyResult> {
  const envelope = await postEnvelope<{ result: ScannerVerifyResult }>("/api/scanner/verify", {
    ticketCode,
    eventId,
    source
  });
  return envelope.data.result;
}

export async function checkInScannerTicket(
  ticketCode: string,
  eventId: string,
  source: "camera" | "manual" = "manual"
): Promise<{ result: ScannerVerifyResult; stats: ScannerStats }> {
  const envelope = await postEnvelope<{ result: ScannerVerifyResult; stats: ScannerStats }>("/api/scanner/check-in", {
    ticketCode,
    eventId,
    source
  });
  return envelope.data;
}

export async function checkOutScannerTicket(
  ticketCode: string,
  eventId: string,
  source: "camera" | "manual" = "manual"
): Promise<{ result: ScannerVerifyResult; stats: ScannerStats }> {
  const envelope = await postEnvelope<{ result: ScannerVerifyResult; stats: ScannerStats }>("/api/scanner/check-out", {
    ticketCode,
    eventId,
    source
  });
  return envelope.data;
}

export async function getScannerHistory(eventId: string, ticketCode?: string): Promise<{ history: ScannerHistoryEntry[]; stats: ScannerStats }> {
  const params = new URLSearchParams({ eventId });
  if (ticketCode) params.set("ticketCode", ticketCode);
  const envelope = await getEnvelope<{ history: ScannerHistoryEntry[]; stats: ScannerStats }>(`/api/scanner/history?${params}`);
  return envelope?.data ?? {
    history: [],
    stats: { eventId, validTickets: 0, checkedInNow: 0, checkedOutCount: 0, invalidScanCount: 0, recentActivity: [] }
  };
}
